/**
 * An error carrying the HTTP status that produced it.
 *
 * The status has to survive as far as the retry policy. Without it, a 401 looks identical to a
 * dropped connection, and the client burns retries on a request that can never succeed while the
 * reader stares at a spinner.
 */
export class ApiError extends Error {
  /** The HTTP status returned by the API. */
  public readonly status: number;

  /**
   * Creates an API error.
   *
   * @param status - The HTTP status returned by the API.
   * @param message - A message suitable for display.
   */
  public constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }

  /**
   * Whether retrying could plausibly succeed.
   *
   * 4xx means the request itself is unacceptable -- unauthenticated, forbidden, malformed. Sending
   * it again unchanged produces the same answer. 5xx and transport failures are worth another go,
   * which at an event venue is the common case.
   */
  public get isRetryable(): boolean {
    return this.status >= 500 || this.status === 408 || this.status === 429;
  }
}
